/**
 * Geography > Assignments (WPS-222 / WPS-160 C10) - who owns each
 * (Product Template, Geo Zone) pair for claim routing.
 *
 * Rows are zones; one owner column per routing channel the tenant's own ZONE
 * rules declare (a "Platinum" overlay is simply a second channel). The
 * template scope picker switches between the "all templates" owners and the
 * owners scoped to one product template. Changing an owner previews the open
 * claims that would move and asks for confirmation before they do.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { AlertTriangle, Users } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Label } from '@/shared/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import type { ProductFilterFacet } from '@/shared/components/ui/product-filter-menu'
import type { FormFieldMeta } from '@/shared/lib/forms'
import { toastApiError } from '@/shared/lib/api-error'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { EmptyState } from '@/admin/components/shared/empty-state'
import { EditFormDialog } from '@/admin/components/shared/edit-form-dialog'
import { usePermissions } from '@/admin/hooks/use-permissions'
import {
  useGeoAssignmentMembers,
  useGeoAssignments,
  useGeoMeta,
  usePreviewOwnerChange,
  useSetOwner,
  type AssignmentChannel,
  type AssignmentZoneRow,
  type OwnerCell,
  type OwnerChangeInput,
  type OwnerChangePreview,
} from '@/admin/hooks/use-geo-reference'

import { GeographySectionShell, TextCell } from './geography-shared'
import { useCountLine, useZoneTypeLabel, zoneLabel } from './geography-utils'
import { NONE, nullable, saveWithFieldErrors, useListState } from './section-crud'

const ALL_TEMPLATES = '__all__'

interface PendingChange {
  input: OwnerChangeInput
  preview: OwnerChangePreview
  zoneName: string
  channelLabel: string
}

export function AssignmentsSection() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const canEdit = can('geo-assignments.edit')
  const typeLabel = useZoneTypeLabel()

  const list = useListState()
  const [templateScope, setTemplateScope] = useState<string>(ALL_TEMPLATES)
  const [channelFilter, setChannelFilter] = useState<string>('')
  const [unassignedOnly, setUnassignedOnly] = useState(false)
  const [assigning, setAssigning] = useState<{ row: AssignmentZoneRow; channel: AssignmentChannel } | null>(null)
  const [pending, setPending] = useState<PendingChange | null>(null)
  const [moveClaims, setMoveClaims] = useState(true)

  const { data: meta, isPending: metaPending } = useGeoMeta()
  const params = useMemo(
    () => ({
      ...list.params,
      product_template: templateScope === ALL_TEMPLATES ? undefined : templateScope,
      channel: channelFilter || undefined,
      unassigned: unassignedOnly && channelFilter ? '1' : undefined,
    }),
    [channelFilter, list.params, templateScope, unassignedOnly],
  )
  const { data, isLoading, isFetching, isError, refetch } = useGeoAssignments(params)
  const preview = usePreviewOwnerChange()
  const setOwner = useSetOwner()
  const { data: members } = useGeoAssignmentMembers(assigning?.channel.key ?? null)

  const channels = data?.channels ?? meta?.channels ?? []
  const rows = data?.results ?? []
  const countLine = useCountLine(
    data?.count,
    t('geography.zones.countOne', 'zone'),
    t('geography.zones.countMany', 'zones'),
  )

  const facets = useMemo<ProductFilterFacet[]>(
    () => [
      {
        key: 'zone_type',
        label: t('geography.zones.field.zoneType', 'Zone type'),
        kind: 'field',
        options: meta?.zone_types ?? [],
      },
    ],
    [meta?.zone_types, t],
  )

  const memberOptions = useMemo(
    () => [
      { value: NONE, label: t('geography.assignments.unassign', 'Unassign') },
      ...(members ?? []).map((m) => ({
        value: m.membership_uuid,
        label: m.is_wildcard
          ? `${m.name} (${m.email}) - ${t('geography.assignments.allZonesShort', 'all zones')}`
          : `${m.name} (${m.email})`,
      })),
    ],
    [members, t],
  )

  const assignFields = useMemo<FormFieldMeta[]>(
    () => [
      {
        code_name: 'membership_uuid',
        display_name: assigning?.channel.label ?? t('geography.assignments.owner', 'Owner'),
        field_type: 'SELECT',
        required: true,
        options: memberOptions,
        configuration_data: { searchable: true },
        description: assigning
          ? t(
              'geography.assignments.ownerHint',
              'Members holding the {{role}} role. Selecting Unassign clears the pair.',
              { role: assigning.channel.role },
            )
          : undefined,
      },
    ],
    [assigning, memberOptions, t],
  )
  const assignValues = useMemo<Record<string, unknown>>(() => {
    if (!assigning) return {}
    const current = assigning.row.owners[assigning.channel.key]
    return { membership_uuid: current?.membership_uuid ?? NONE }
  }, [assigning])

  function ownerCell(row: AssignmentZoneRow, channel: AssignmentChannel) {
    const owner: OwnerCell | null = row.owners[channel.key] ?? null
    const open = () => setAssigning({ row, channel })
    if (!owner) {
      return (
        <Button
          variant="ghost"
          size="xs"
          className="text-[var(--ink-3)]"
          disabled={!canEdit}
          data-testid={`geo-assign-${row.zone_uuid}-${channel.channel_key || 'default'}`}
          onClick={open}
        >
          {t('geography.assignments.unassigned', 'Unassigned')}
        </Button>
      )
    }
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <Button
          variant="ghost"
          size="xs"
          disabled={!canEdit}
          data-testid={`geo-assign-${row.zone_uuid}-${channel.channel_key || 'default'}`}
          onClick={open}
        >
          {owner.name}
        </Button>
        {owner.product_template ? (
          <Badge variant="secondary">{owner.product_template.name}</Badge>
        ) : null}
        {owner.via === 'attribute' ? (
          <Badge variant="outline">{t('geography.assignments.viaProfile', 'profile')}</Badge>
        ) : null}
        {owner.via === 'legacy' ? (
          <Badge variant="outline">{t('geography.assignments.viaLegacy', 'legacy')}</Badge>
        ) : null}
        {owner.conflict ? (
          <Badge variant="destructive" title={(owner.others ?? []).map((o) => o.name).join(', ')}>
            {t('geography.assignments.conflict', 'Conflict')}
          </Badge>
        ) : null}
      </div>
    )
  }

  const columns = useMemo<DataTableColumn<AssignmentZoneRow>[]>(
    () => [
      {
        id: 'zone',
        header: t('geography.zones.column.name', 'Zone'),
        sortable: true,
        sortField: 'name',
        cell: (r) => <TextCell value={zoneLabel(r.zone_name, r.zone_code)} />,
      },
      {
        id: 'zone_type',
        header: t('geography.zones.column.type', 'Type'),
        cell: (r) => <TextCell muted value={typeLabel(r.zone_type)} />,
      },
      ...channels.map<DataTableColumn<AssignmentZoneRow>>((channel) => ({
        id: `owner-${channel.key}`,
        header: channel.label,
        cell: (r) => ownerCell(r, channel),
      })),
    ],
    // ownerCell closes over canEdit/t; channels drives the column set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canEdit, channels, t, typeLabel],
  )

  async function handleAssign(v: Record<string, unknown>, helpers?: { setFieldError: (f: string, m: string | undefined) => void }) {
    if (!assigning) return
    const input: OwnerChangeInput = {
      zone_uuid: assigning.row.zone_uuid,
      channel: assigning.channel.key,
      product_template_uuid: templateScope === ALL_TEMPLATES ? null : templateScope,
      membership_uuid: nullable(v.membership_uuid),
    }
    const zoneName = zoneLabel(assigning.row.zone_name, assigning.row.zone_code)
    const channelLabel = assigning.channel.label
    if (input.membership_uuid) {
      // Preview first: when open claims would move, hand off to the confirm
      // step (the form dialog closes on resolve) - nothing is written and no
      // success toast fires until the operator confirms.
      let result: OwnerChangePreview
      try {
        result = await preview.mutateAsync(input)
      } catch (err) {
        toastApiError(err, t('geography.assignments.previewFailed', 'Could not preview the change.'))
        throw err
      }
      if (result.count > 0) {
        setMoveClaims(true)
        setPending({ input, preview: result, zoneName, channelLabel })
        return
      }
    }
    await saveWithFieldErrors(
      () => setOwner.mutateAsync({ ...input, reassign_open_claims: false }),
      helpers,
      input.membership_uuid
        ? t('geography.assignments.updated', 'Owner updated.')
        : t('geography.assignments.cleared', 'Owner cleared.'),
      t('geography.assignments.saveFailed', 'Could not change the owner.'),
    )
  }

  async function confirmPending() {
    if (!pending) return
    try {
      const result = await setOwner.mutateAsync({
        ...pending.input,
        reassign_open_claims: moveClaims,
      })
      toast.success(
        moveClaims
          ? t('geography.assignments.movedClaims', 'Owner updated and {{count}} open claim(s) moved.', {
              count: result.reassigned,
            })
          : t('geography.assignments.updated', 'Owner updated.'),
      )
      setPending(null)
    } catch (err) {
      toastApiError(err, t('geography.assignments.saveFailed', 'Could not change the owner.'))
    }
  }

  const noChannels = !metaPending && channels.length === 0

  return (
    <>
      <GeographySectionShell
        testId="geography-assignments"
        title={t('geography.assignments.title', 'Assignments')}
        description={t(
          'geography.assignments.description',
          "Who owns each zone for claim routing, per product template. A pair has exactly one owner; changing it can move that pair's open claims to the new owner.",
        )}
        countLine={noChannels ? undefined : countLine}
        search={noChannels ? undefined : list.search}
        onSearchChange={noChannels ? undefined : list.setSearch}
        searchPlaceholder={t('geography.zones.searchPlaceholder', 'Search by name or code')}
        facets={noChannels ? undefined : facets}
        filterValues={list.filterValues}
        onFilterValuesChange={list.setFilterValues}
        actions={
          noChannels ? null : (
            <>
              <div className="flex items-center gap-2">
                <Label className="text-[12px] text-[var(--ink-3)]">
                  {t('geography.assignments.templateScope', 'Template')}
                </Label>
                <Select value={templateScope} onValueChange={setTemplateScope}>
                  <SelectTrigger className="h-[34px] min-w-[180px]" data-testid="geo-assign-template-scope">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_TEMPLATES}>
                      {t('geography.assignments.allTemplates', 'All templates')}
                    </SelectItem>
                    {(meta?.product_templates ?? []).map((p) => (
                      <SelectItem key={p.uuid} value={p.uuid}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {channels.length > 1 ? (
                <Select
                  value={channelFilter || '__any__'}
                  onValueChange={(v) => {
                    setChannelFilter(v === '__any__' ? '' : v)
                    if (v === '__any__') setUnassignedOnly(false)
                  }}
                >
                  <SelectTrigger className="h-[34px] min-w-[160px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any__">{t('geography.assignments.allChannels', 'All channels')}</SelectItem>
                    {channels.map((c) => (
                      <SelectItem key={c.key} value={c.key}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
              {channelFilter || channels.length === 1 ? (
                <Label className="flex items-center gap-2 text-[12px] text-[var(--ink-2)]">
                  <Checkbox
                    checked={unassignedOnly}
                    onCheckedChange={(v) => {
                      setUnassignedOnly(Boolean(v))
                      if (!channelFilter && channels.length === 1) setChannelFilter(channels[0].key)
                    }}
                  />
                  {t('geography.assignments.unassignedOnly', 'Unassigned only')}
                </Label>
              ) : null}
            </>
          )
        }
      >
        {noChannels ? (
          <EmptyState
            icon={Users}
            title={t('geography.assignments.noChannelsTitle', 'No assignment channels yet')}
            description={t(
              'geography.assignments.noChannelsDescription',
              'Zone ownership follows your claim routing rules. Add a rule that assigns by zone owner under Settings > Claims > Routing and the channel appears here.',
            )}
          />
        ) : (
          <>
            {(data?.wildcard_holders?.length ?? 0) > 0 ? (
              <div className="flex flex-wrap items-center gap-2 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-4 py-3 text-[13px]">
                <AlertTriangle className="h-4 w-4 text-[var(--ink-3)]" />
                <span className="text-[var(--ink-2)]">
                  {t('geography.assignments.wildcardTitle', 'Covers every zone:')}
                </span>
                {data!.wildcard_holders.map((w) => {
                  const channel = channels.find((c) => c.key === w.channel)
                  return (
                    <Badge key={`${w.channel}-${w.membership_uuid}`} variant="secondary">
                      {w.name}
                      {channel ? ` - ${channel.label}` : ''}
                    </Badge>
                  )
                })}
              </div>
            ) : null}
            <DataTable<AssignmentZoneRow>
              tableId="geography-assignments"
              columns={columns}
              data={rows}
              isLoading={isLoading}
              isFetching={isFetching}
              isError={isError}
              onRetry={() => refetch()}
              page={list.page}
              pageSize={list.pageSize}
              totalCount={data?.count ?? 0}
              onPageChange={list.setPage}
              onPageSizeChange={list.setPageSize}
              rowKey={(r) => r.zone_uuid}
              emptyStateNode={
                <EmptyState
                  icon={Users}
                  title={
                    unassignedOnly
                      ? t('geography.assignments.allAssignedTitle', 'Every zone has an owner')
                      : t('geography.zones.emptyTitle', 'No zones yet')
                  }
                />
              }
            />
            <p className="text-[12px] text-[var(--ink-3)]">
              {t('geography.assignments.coverHint', 'Out-of-office cover is managed under')}{' '}
              <Link to="/settings/claims/routing" className="underline">
                {t('geography.assignments.coverLink', 'Settings > Claims > Routing')}
              </Link>
              .
            </p>
          </>
        )}
      </GeographySectionShell>

      <EditFormDialog
        open={assigning !== null}
        onOpenChange={(open) => {
          if (!open) setAssigning(null)
        }}
        title={
          assigning
            ? t('geography.assignments.setOwnerTitle', 'Owner for {{zone}}', {
                zone: zoneLabel(assigning.row.zone_name, assigning.row.zone_code),
              })
            : ''
        }
        fields={assignFields}
        values={assignValues}
        isPending={preview.isPending || setOwner.isPending}
        onSave={(v, _reason, helpers) => handleAssign(v, helpers)}
      />

      <Dialog open={pending !== null} onOpenChange={(open) => { if (!open) setPending(null) }}>
        <DialogContent className="max-w-[560px]">
          <DialogHeader>
            <DialogTitle>
              {t('geography.assignments.confirmTitle', 'Move open claims to the new owner?')}
            </DialogTitle>
            <DialogDescription>
              {pending
                ? t(
                    'geography.assignments.confirmDescription',
                    '{{count}} open claim(s) for {{zone}} ({{channel}}) currently sit with {{previous}}. They can move to {{next}} now; future claims route to {{next}} either way.',
                    {
                      count: pending.preview.count,
                      zone: pending.zoneName,
                      channel: pending.channelLabel,
                      previous:
                        pending.preview.previous_owner?.name ??
                        t('geography.assignments.nobody', 'nobody'),
                      next: pending.preview.new_owner?.name ?? '',
                    },
                  )
                : null}
            </DialogDescription>
          </DialogHeader>
          {pending ? (
            <div className="max-h-[240px] overflow-y-auto rounded-[8px] border-[0.5px] border-[var(--rule)]" data-testid="geo-assign-preview-list">
              {pending.preview.claims.map((c) => (
                <div
                  key={c.uuid}
                  className="flex items-center justify-between gap-3 border-b-[0.5px] border-b-[var(--rule-light)] px-3 py-2 text-[13px] last:border-b-0"
                >
                  <span className="text-[var(--ink)]">{c.number || c.uuid.slice(0, 8)}</span>
                  <span className="text-[var(--ink-3)]">{c.state_label}</span>
                </div>
              ))}
            </div>
          ) : null}
          <Label className="flex items-center gap-2 text-[13px] text-[var(--ink)]">
            <Checkbox
              checked={moveClaims}
              onCheckedChange={(v) => setMoveClaims(Boolean(v))}
              data-testid="geo-assign-move-claims"
            />
            {t('geography.assignments.moveClaimsLabel', 'Move these open claims to the new owner')}
          </Label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPending(null)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              type="button"
              onClick={confirmPending}
              disabled={setOwner.isPending}
              data-testid="geo-assign-confirm"
            >
              {setOwner.isPending
                ? t('geography.common.saving', 'Saving...')
                : t('geography.assignments.confirm', 'Confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
